import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, MessageCircle, Wind, BookOpen, Anchor, LogOut, RefreshCcw, Layers, BarChart3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { MammothMascot } from "./MammothMascot";
import { BrandLogo } from "./BrandLogo";

const navItems = [
  { to: "/dashboard", icon: Home, label: "Home", tid: "nav-home" },
  { to: "/coach", icon: MessageCircle, label: "Coach", tid: "nav-coach" },
  { to: "/breathing", icon: Wind, label: "Breathe", tid: "nav-breathe" },
  { to: "/grounding", icon: Anchor, label: "Ground", tid: "nav-ground" },
  { to: "/cbt", icon: RefreshCcw, label: "Reframe", tid: "nav-cbt" },
  { to: "/journal", icon: BookOpen, label: "Journal", tid: "nav-journal" },
  { to: "/levels", icon: Layers, label: "Stages", tid: "nav-levels" },
  { to: "/sessions", icon: BarChart3, label: "Sessions", tid: "nav-sessions" },
];

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const lvl = levelFor(user?.current_level || 1);

  return (
    <div className="min-h-screen relative grain transition-colors duration-700" style={{ backgroundColor: lvl.background, color: lvl.text }}>
      {/* Top bar */}
      <header className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-start" data-testid="brand-link">
          <BrandLogo width="clamp(130px, 14vw, 200px)" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 bg-white/70 backdrop-blur rounded-full px-4 py-2 shadow-sm">
            <span className="text-xs font-bold uppercase tracking-widest opacity-60">Stage {lvl.level}</span>
            <span className="font-display font-bold">{lvl.name}</span>
          </div>
          <button onClick={() => { logout(); nav("/"); }} className="btn-pill bg-[#0e203b] text-white rounded-full px-4 h-10 text-sm font-semibold flex items-center gap-2" data-testid="logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        {children}
      </main>

      {/* Bottom nav (sticky) */}
      <nav className="fixed bottom-4 left-1/2 -translate-x-1/2 z-20 bg-[#0e203b] text-white rounded-full px-2 py-2 shadow-2xl flex items-center gap-1" data-testid="bottom-nav">
        {navItems.map((it) => {
          const active = loc.pathname.startsWith(it.to);
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              className={`flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-full text-xs font-semibold transition-all ${
                active ? "bg-white text-[#0e203b]" : "text-white/70 hover:text-white"
              }`}
            >
              <Icon size={16} strokeWidth={2.5} />
              <span className="hidden sm:inline">{it.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default AppShell;
